import { Box, Tooltip, Typography } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import { Deal, DealType } from "src/business/deal";
import {
  colorBackgroundInfo,
  colorBorder,
  colorBorderHover,
  colorBorderInfo,
  colorSlotDeal,
  colorSlotDealHover,
  colorSlotEvent,
  colorSlotEventHover,
  colorSurface,
  colorSurfaceHover,
} from "src/design/tokens";
import {
  BookingSegment,
  DayView,
  FULL_DAY_VIEW,
  OpenWindow,
  isRowOpen,
  viewLengthPercent,
  viewTopPercent,
} from "../dayGridHelpers";
import { BookingBlock } from "./BookingBlock";

// An empty slot inside a deal window takes the gold tint, an event window the
// purple one — the same scale the rest of the app uses for offers.
const OFFER_TINTS: Record<DealType, { bg: string; hover: string }> = {
  [DealType.DEAL]: { bg: colorSlotDeal, hover: colorSlotDealHover },
  [DealType.EVENT]: { bg: colorSlotEvent, hover: colorSlotEventHover },
};

export interface DropPreview {
  table: number;
  startRow: number;
  rows: number;
}

interface OfferTooltipProps {
  deal: Deal;
}

// `content` is stored as "name\ndescription…" — the first line names the offer.
const OfferTooltip = ({ deal }: OfferTooltipProps) => {
  const [name, ...rest] = deal.content.split("\n");
  const description = rest.join("\n").trim();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{name}</Typography>
      {description && (
        <Typography sx={{ fontSize: 12.5, whiteSpace: "pre-line" }}>
          {description}
        </Typography>
      )}
    </Box>
  );
};

interface TableColumnProps {
  table: number;
  segments: BookingSegment[];
  openWindows: OpenWindow[];
  /** Deal or event covering each row of the day, or undefined where none does. */
  dealRows: (Deal | undefined)[];
  /** Rows to render; defaults to the whole day. */
  view?: DayView;
  isPlacing: boolean;
  canCreate: boolean;
  draggingBookingId: string | null;
  dropPreview: DropPreview | null;
  isLastColumn: boolean;
  onSlotClick: (table: number, row: number) => void;
  onBlockPointerDown: (
    event: React.PointerEvent,
    segment: BookingSegment,
  ) => void;
  onBlockClick: (segment: BookingSegment) => void;
  sx?: SxProps;
}

/**
 * One table's column: a continuous stack of half-hour rows separated by thin
 * dividers, tinted for opening hours and for any deal or event covering the
 * slot, with its bookings drawn on top. Rows outside opening hours stay plain
 * but remain fully usable — placement is never gated on the schedule.
 */
export const TableColumn = ({
  table,
  segments,
  openWindows,
  dealRows,
  view = FULL_DAY_VIEW,
  isPlacing,
  canCreate,
  draggingBookingId,
  dropPreview,
  isLastColumn,
  onSlotClick,
  onBlockPointerDown,
  onBlockClick,
  sx,
}: TableColumnProps) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      position: "relative",
      display: "flex",
      flexDirection: "column",
      borderRight: isLastColumn ? "none" : `1px solid ${colorBorder}`,
      ...sx,
    }}
  >
    {Array.from({ length: view.endRow - view.startRow }, (_, index) => {
      const row = view.startRow + index;
      const open = isRowOpen(openWindows, row);
      const deal = dealRows[row];
      const tint = deal ? OFFER_TINTS[deal.type] : null;
      // A heavier rule on the hour keeps 48 rows scannable.
      const isHourBoundary = row % 2 === 1;
      const clickable = canCreate || isPlacing;

      const cell = (
        <Box
          key={`cell-${table}-${row}`}
          onClick={clickable ? () => onSlotClick(table, row) : undefined}
          sx={{
            flex: 1,
            minHeight: 0,
            backgroundColor: tint
              ? tint.bg
              : open
                ? colorBackgroundInfo
                : colorSurface,
            borderBottom: `1px ${isHourBoundary ? "solid" : "dotted"} ${
              isHourBoundary ? colorBorder : colorBorderHover
            }`,
            cursor: clickable ? "pointer" : "default",
            ":hover": clickable
              ? { backgroundColor: tint ? tint.hover : colorSurfaceHover }
              : undefined,
          }}
        />
      );

      // Only slots carrying an offer get a tooltip — the rest stay plain nodes.
      return deal ? (
        <Tooltip
          key={`offer-${table}-${row}`}
          title={<OfferTooltip deal={deal} />}
          arrow
          placement="right"
          disableInteractive
        >
          {cell}
        </Tooltip>
      ) : (
        cell
      );
    })}

    {dropPreview && dropPreview.table === table && (
      <Box
        sx={{
          position: "absolute",
          top: viewTopPercent(dropPreview.startRow, view),
          height: viewLengthPercent(dropPreview.rows, view),
          left: 0,
          right: 0,
          border: `2px dashed ${colorBorderInfo}`,
          borderRadius: 1,
          pointerEvents: "none",
          zIndex: 3,
        }}
      />
    )}

    {segments.map((segment) => (
      <BookingBlock
        key={segment.booking.id}
        segment={segment}
        view={view}
        isDragging={draggingBookingId === segment.booking.id}
        onPointerDown={(event) => onBlockPointerDown(event, segment)}
        onClick={() => onBlockClick(segment)}
      />
    ))}
  </Box>
);
